import React, { useEffect, useState } from 'react';
import {
    Eye, MessageSquare, Heart, DollarSign, FileText, Video, Map, Box, RefreshCw,
} from 'lucide-react';
import { getReaListingPerformance } from '../../../api/agent';

/**
 * realestate.com.au campaign performance for one listing, headlined by
 * Property Price Statement (PPS) views. Fed by REA's Campaign API via the
 * CRM backend (12h cache); self-contained so a REA outage never touches the
 * rest of the analytics dashboard.
 */

const METRICS = [
    { key: 'pps_views', label: 'PPS views', icon: FileText, headline: true },
    { key: 'page_views', label: 'Page views', icon: Eye },
    { key: 'enquiries', label: 'Enquiries', icon: MessageSquare },
    { key: 'saves', label: 'Saves', icon: Heart },
    { key: 'video_views', label: 'Video views', icon: Video },
    { key: 'floorplan_views', label: 'Floorplan views', icon: Map },
    { key: 'tour_3d_views', label: '3D tour views', icon: Box },
    { key: 'offers_received', label: 'Offers received', icon: DollarSign },
];

export default function ReaPerformancePanel({ reaListingId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!reaListingId) { setLoading(false); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await getReaListingPerformance(reaListingId);
                if (cancelled) return;
                if (res?.data?.success) setData(res.data);
                else setFailed(true);
            } catch {
                if (!cancelled) setFailed(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [reaListingId]);

    if (!reaListingId) return null;

    if (loading) {
        return (
            <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white p-4 text-xs text-gray-400">
                <RefreshCw size={12} className="animate-spin" /> Loading realestate.com.au stats…
            </div>
        );
    }

    if (failed || !data) {
        return (
            <p className="rounded-xl border border-gray-200 bg-white p-4 text-xs text-gray-400">
                realestate.com.au stats are unavailable for this listing right now.
            </p>
        );
    }

    const totals = data.campaign_total || {};
    const last7 = data.last_7_days;

    return (
        <div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {METRICS.map(({ key, label, icon: Icon, headline }) => (
                    <div
                        key={key}
                        className={`rounded-xl border p-3 ${headline
                            ? 'border-[#55C5D0] bg-[#f0fafb]'
                            : 'border-gray-200 bg-white'}`}
                    >
                        <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-gray-500">
                            <Icon size={12} className="text-[#55C5D0]" /> {label}
                        </span>
                        <p className="mt-1 text-lg font-extrabold text-[#374151]">
                            {(totals[key] ?? 0).toLocaleString()}
                        </p>
                        {last7 && (
                            <p className="text-[11px] font-medium text-gray-400">
                                {(last7[key] ?? 0).toLocaleString()} in the last 7 days
                            </p>
                        )}
                    </div>
                ))}
            </div>
            {data.data_as_of && (
                <p className="mt-2 text-[11px] text-gray-400">
                    realestate.com.au data as of {data.data_as_of}. PPS views count buyers who
                    opened the Property Price Statement on the listing.
                </p>
            )}
        </div>
    );
}
